import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Input from '../../../../components/Input';
import TextArea from '../../../../components/TextArea';
import Button from '../../../../components/Button';
import Toast from '../../../../components/Toast';
import Spinner from '../../../../components/Spinner';
import ReceiptTemplateService, { ReceiptTemplateFormPayload } from '../../../../services/receipt-template.service';
import ProductService from '../../../../services/product.service';
import { Modules, AuthRoutes } from '../../../../enums';

const TOKENS = [
  '{{receipt_no}}',
  '{{tenant_name}}',
  '{{plan_name}}',
  '{{amount}}',
  '{{currency}}',
  '{{payment_date}}',
  '{{payment_mode}}',
  '{{reference_no}}',
];

interface ProductOption {
  id: number;
  product_code: string;
  product_name: string;
}

const initialForm: ReceiptTemplateFormPayload = {
  template_code: '',
  template_name: '',
  header: '',
  footer: '',
  body: '',
  product_id: undefined,
  status: 'active',
  notes: '',
};

const ReceiptTemplateForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const edit = !!id;

  const [formData, setFormData] = useState<ReceiptTemplateFormPayload>(initialForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(edit);
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [showTokens, setShowTokens] = useState(false);

  useEffect(() => {
    ProductService.list({ includeDeleted: false, limit: 100 }).then((response: any) => {
      setProducts(response?.data?.items ?? []);
    });
  }, []);

  useEffect(() => {
    if (!id) {
      setFormData(initialForm);
      return;
    }
    setFetching(true);
    ReceiptTemplateService.get(id)
      .then((response: any) => {
        const row = response?.data ?? {};
        setFormData({
          template_code: row.template_code ?? '',
          template_name: row.template_name ?? '',
          header: row.header ?? '',
          footer: row.footer ?? '',
          body: row.body ?? '',
          product_id: row.product_id ?? undefined,
          status: row.status ?? 'active',
          notes: row.notes ?? '',
        });
      })
      .finally(() => setFetching(false));
  }, [id]);

  const validate = useCallback(
    (data: ReceiptTemplateFormPayload) => {
      const nextErrors: Record<string, string> = {};
      if (!data.template_code.trim()) nextErrors.template_code = t('masters.receiptTemplates.form.pageTexts.fields.templateCode.errors.required');
      if (!data.template_name.trim()) nextErrors.template_name = t('masters.receiptTemplates.form.pageTexts.fields.templateName.errors.required');
      if (!data.body.trim()) nextErrors.body = t('masters.receiptTemplates.form.pageTexts.fields.body.errors.required');
      return nextErrors;
    },
    [t]
  );

  const handleChange = useCallback((e: any, name: string) => {
    const value = name === 'product_id' ? (e.target.value === '' ? undefined : Number(e.target.value)) : e.target.value;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e && e.preventDefault();
      const nextErrors = validate(formData);
      setErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) return;

      setLoading(true);
      try {
        const payload = { ...formData };
        if (edit && id) {
          await ReceiptTemplateService.update(id, payload);
          toast.success(<Toast message={t('masters.receiptTemplates.form.pageTexts.editSuccess')} />, { icon: false });
        } else {
          await ReceiptTemplateService.create(payload);
          toast.success(<Toast message={t('masters.receiptTemplates.form.pageTexts.addSuccess')} />, { icon: false });
        }
        navigate(`${Modules.masters}${AuthRoutes.receiptTemplates}`);
      } catch (err) {
        setLoading(false);
        return;
      }
      setLoading(false);
    },
    [formData, edit, id, navigate, validate, t]
  );

  if (fetching) {
    return (
      <div className="card-header text-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="card mb-6">
      <h5 className="card-header">
        {edit ? t('masters.receiptTemplates.form.pageTexts.editTitle') : t('masters.receiptTemplates.form.pageTexts.addTitle')}
      </h5>
      <hr className="m-0" />
      <form className="card-body row g-4" onSubmit={handleSubmit}>
        <div className="col-md-4">
          <Input
            id="template_code"
            name="template_code"
            label={t('masters.receiptTemplates.form.pageTexts.fields.templateCode.label')}
            placeholder={t('masters.receiptTemplates.form.pageTexts.fields.templateCode.placeholder')}
            required
            value={formData.template_code}
            error={!!errors.template_code}
            errorMessage={errors.template_code}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-4">
          <Input
            id="template_name"
            name="template_name"
            label={t('masters.receiptTemplates.form.pageTexts.fields.templateName.label')}
            placeholder={t('masters.receiptTemplates.form.pageTexts.fields.templateName.placeholder')}
            required
            value={formData.template_name}
            error={!!errors.template_name}
            errorMessage={errors.template_name}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-2">
          <label className="form-label d-block" htmlFor="product_id">
            {t('masters.receiptTemplates.form.pageTexts.fields.product.label')}
          </label>
          <select
            id="product_id"
            name="product_id"
            className="form-select"
            value={formData.product_id ?? ''}
            onChange={(e) => handleChange(e, 'product_id')}
          >
            <option value="">{t('masters.receiptTemplates.form.pageTexts.fields.product.globalOption')}</option>
            {products.map((product) => (
              <option key={product.id} value={product.id}>
                {product.product_code}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-2">
          <label className="form-label d-block" htmlFor="status">
            {t('masters.receiptTemplates.form.pageTexts.fields.status.label')}
          </label>
          <select id="status" name="status" className="form-select" value={formData.status} onChange={(e) => handleChange(e, 'status')}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        <div className="col-md-6">
          <label className="form-label d-block" htmlFor="header">
            {t('masters.receiptTemplates.form.pageTexts.fields.header.label')}
          </label>
          <TextArea
            id="header"
            name="header"
            rows={3}
            placeholder={t('masters.receiptTemplates.form.pageTexts.fields.header.placeholder')}
            value={formData.header}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-6">
          <label className="form-label d-block" htmlFor="footer">
            {t('masters.receiptTemplates.form.pageTexts.fields.footer.label')}
          </label>
          <TextArea
            id="footer"
            name="footer"
            rows={3}
            placeholder={t('masters.receiptTemplates.form.pageTexts.fields.footer.placeholder')}
            value={formData.footer}
            onChange={handleChange}
          />
        </div>
        <div className="col-12">
          <label className="form-label d-block" htmlFor="body">
            {t('masters.receiptTemplates.form.pageTexts.fields.body.label')}
          </label>
          <TextArea
            id="body"
            name="body"
            rows={8}
            placeholder={t('masters.receiptTemplates.form.pageTexts.fields.body.placeholder')}
            value={formData.body}
            error={!!errors.body}
            errorMessage={errors.body}
            onChange={handleChange}
          />
        </div>

        <div className="col-12">
          <button type="button" className="btn btn-link p-0" onClick={() => setShowTokens((v) => !v)}>
            {showTokens
              ? t('masters.receiptTemplates.form.pageTexts.tokenPanel.hide')
              : t('masters.receiptTemplates.form.pageTexts.tokenPanel.show')}
          </button>
          {showTokens && (
            <div className="border rounded p-3 mt-2">
              <p className="mb-2">{t('masters.receiptTemplates.form.pageTexts.tokenPanel.description')}</p>
              <div className="d-flex flex-wrap gap-2">
                {TOKENS.map((token) => (
                  <code key={token} className="badge bg-label-info">
                    {token}
                  </code>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="col-md-8">
          <Input
            id="notes"
            name="notes"
            label={t('masters.receiptTemplates.form.pageTexts.fields.notes.label')}
            placeholder={t('masters.receiptTemplates.form.pageTexts.fields.notes.placeholder')}
            value={formData.notes}
            onChange={handleChange}
          />
        </div>

        <div className="col-12 d-flex gap-2 justify-content-end">
          <Button
            type="button"
            className="btn btn-outline-secondary"
            text={t('masters.receiptTemplates.form.pageTexts.buttons.cancel')}
            onClick={() => navigate(`${Modules.masters}${AuthRoutes.receiptTemplates}`)}
          />
          <Button
            type="submit"
            className="btn btn-primary"
            text={t('masters.receiptTemplates.form.pageTexts.buttons.save')}
            loading={loading}
          />
        </div>
      </form>
    </div>
  );
};

export default ReceiptTemplateForm;
